import { useEffect, useState } from "react";
import { Columns3, Package, SlidersHorizontal, UserCheck, X } from "lucide-react";
import { ORDER_TAKEN_BY, type OrderTakenBy } from "@school-sales-ai/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { useCatalogContext } from "@/hooks/useCatalog";
import type { OrderBoardColumn } from "@/lib/order-board";
import { useOrdersStore } from "@/stores/orders.store";

const TAKEN_BY_LABELS: Record<OrderTakenBy, string> = {
  agent: "El agente",
  team: "El equipo",
};

/**
 * The single filter bar for /pedidos — it drives the board and the table alike,
 * so switching view keeps whatever is selected (the `LeadFilters` contract).
 *
 * Every categorical filter takes several values: values inside one are OR'd,
 * separate filters AND'd. On the board the column filter hides columns rather
 * than refetching, so the badges keep counting every order.
 */
export function OrderFilters({ columns }: { columns: OrderBoardColumn[] }): React.JSX.Element {
  const search = useOrdersStore((s) => s.search);
  const from = useOrdersStore((s) => s.from);
  const to = useOrdersStore((s) => s.to);
  const selectedColumns = useOrdersStore((s) => s.columns);
  const takenBy = useOrdersStore((s) => s.takenBy);
  const productIds = useOrdersStore((s) => s.productIds);
  const setFilter = useOrdersStore((s) => s.setFilter);
  const toggleColumn = useOrdersStore((s) => s.toggleColumn);
  const clearColumns = useOrdersStore((s) => s.clearColumns);
  const toggleTakenBy = useOrdersStore((s) => s.toggleTakenBy);
  const clearTakenBy = useOrdersStore((s) => s.clearTakenBy);
  const toggleProduct = useOrdersStore((s) => s.toggleProduct);
  const clearProducts = useOrdersStore((s) => s.clearProducts);
  const reset = useOrdersStore((s) => s.reset);

  const { data: catalog } = useCatalogContext();
  const products = catalog?.products ?? [];

  const [searchInput, setSearchInput] = useState(search);
  const [sheetOpen, setSheetOpen] = useState(false);

  // What the mobile sheet holds, counted per selected VALUE: two columns picked
  // is two things narrowing the list, and the badge should say so.
  const activeCount =
    selectedColumns.length +
    takenBy.length +
    productIds.length +
    [from, to].filter(Boolean).length;
  const hasActiveFilters = activeCount > 0 || !!searchInput;

  useEffect(() => {
    const timer = setTimeout(() => setFilter("search", searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, setFilter]);

  // A cleared store (e.g. "Ver todos en Tabla" never touches search, but
  // `reset` does) must clear the box too.
  useEffect(() => {
    if (search === "") setSearchInput("");
  }, [search]);

  function handleClear(): void {
    setSearchInput("");
    reset();
    setSheetOpen(false);
  }

  const searchBox = (
    <Input
      placeholder="Buscar por nombre, teléfono, ref o dirección…"
      value={searchInput}
      onChange={(e) => setSearchInput(e.target.value)}
    />
  );

  const columnFilter = (
    <MultiSelectFilter
      options={columns.map((c) => ({ id: c.key, name: c.label, swatchClass: c.swatchClass }))}
      selected={selectedColumns}
      onToggle={toggleColumn}
      onClear={clearColumns}
      placeholder="Columna"
      pluralNoun="columnas"
      singularNoun="columna"
      clearLabel="Todas las columnas"
      icon={Columns3}
    />
  );

  const takenByFilter = (
    <MultiSelectFilter
      options={ORDER_TAKEN_BY.map((who) => ({ id: who, name: TAKEN_BY_LABELS[who] }))}
      selected={takenBy}
      // The ids are exactly `ORDER_TAKEN_BY`, so the lookup narrows the type
      // without a cast and an unknown id is simply ignored.
      onToggle={(id) => {
        const who = ORDER_TAKEN_BY.find((w) => w === id);
        if (who) toggleTakenBy(who);
      }}
      onClear={clearTakenBy}
      placeholder="Tomado por"
      pluralNoun="opciones"
      singularNoun="opción"
      clearLabel="Cualquiera"
      icon={UserCheck}
    />
  );

  const productFilter =
    products.length > 0 ? (
      <MultiSelectFilter
        options={products.map((p) => ({ id: p.id, name: p.name }))}
        selected={productIds}
        onToggle={toggleProduct}
        onClear={clearProducts}
        placeholder="Producto"
        pluralNoun="productos"
        singularNoun="producto"
        clearLabel="Todos los productos"
        icon={Package}
      />
    ) : null;

  const dateRange = (
    <div className="flex items-center gap-1">
      <Input
        type="date"
        aria-label="Desde"
        value={from}
        onChange={(e) => setFilter("from", e.target.value)}
        className="w-full md:w-[140px]"
      />
      <span className="text-xs text-ink-300">–</span>
      <Input
        type="date"
        aria-label="Hasta"
        value={to}
        onChange={(e) => setFilter("to", e.target.value)}
        className="w-full md:w-[140px]"
      />
    </div>
  );

  return (
    <>
      {/* ── Mobile: search + filter sheet ─────────────── */}
      <div className="md:hidden">
        <div className="flex items-center gap-2 border-b border-ink-100 bg-white px-4 py-2.5">
          <div className="flex-1">{searchBox}</div>
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-2 text-[12px] font-semibold ${
              activeCount > 0 ? "border-moss bg-lime-pale text-moss" : "border-ink-200 text-ink-700"
            }`}
          >
            <SlidersHorizontal size={14} />
            Filtros
            {activeCount > 0 && (
              <span className="rounded-full bg-moss px-1.5 text-[10px] font-bold text-white">
                {activeCount}
              </span>
            )}
          </button>
        </div>

        {sheetOpen && (
          <div className="fixed inset-0 z-50 flex flex-col justify-end">
            <button
              type="button"
              aria-label="Cerrar filtros"
              onClick={() => setSheetOpen(false)}
              className="absolute inset-0 bg-black/30"
            />
            <div className="relative animate-sheet-up rounded-t-2xl bg-white p-4 pb-8">
              <div className="mb-3 flex items-center justify-between">
                <span className="font-display text-[18px] font-bold text-ink-900">Filtros</span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  className="cursor-pointer p-1 text-ink-400"
                  aria-label="Cerrar"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex flex-col gap-3">
                {columnFilter}
                {takenByFilter}
                {productFilter}
                {dateRange}
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" onClick={handleClear} className="mt-3 w-full text-ink-500">
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop: one wrapping row ─────────────────── */}
      <div className="hidden flex-col gap-2 md:flex">
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[180px] flex-1">{searchBox}</div>
          {columnFilter}
          {takenByFilter}
          {productFilter}
          {dateRange}
        </div>
        {hasActiveFilters && (
          <div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="text-ink-400 hover:text-ink-700"
            >
              Limpiar filtros
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
